import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';

const VideoGenerationContext = createContext();

export const useVideoGeneration = () => useContext(VideoGenerationContext);

export const VideoGenerationProvider = ({ children }) => {
    const [isGeneratingReel, setIsGeneratingReel] = useState({ meal: false, pose: false });
    const [generatingReelProgress, setGeneratingReelProgress] = useState({
        meal: { step: '', index: 0, percent: 0 },
        pose: { step: '', index: 0, percent: 0 }
    });
    const [reelVideoUrl, setReelVideoUrl] = useState({ meal: null, pose: null });
    const [globalSessionShots, setGlobalSessionShots] = useState({ meal: [], pose: [] });
    const [globalSelectedShots, setGlobalSelectedShots] = useState({ meal: [], pose: [] });
    const abortControllerRef = useRef(null);

    // --- Canvas Utilities from CalorieMonitorTool ---
    const drawSessionWatermark = (ctx, canvasWidth, canvasHeight, dateRangeStr, type = 'meal') => {
        const padding = Math.round(canvasWidth * 0.05);
        const fontSize = Math.max(18, Math.floor(canvasWidth / 25));

        ctx.save();
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = 8;
        ctx.font = `900 ${fontSize}px Inter, sans-serif`;

        // Bottom Watermark - Poses already have a footer so only add for meals
        if (type !== 'pose') {
            ctx.textAlign = 'right';
            const bottomText = "health monitor @ gk4i.com";
            ctx.fillText(bottomText, canvasWidth - padding, canvasHeight - padding);
        }

        // Top Watermark - Skip for poses as they use a bottom footer
        if (type !== 'pose') {
            ctx.textAlign = 'left';
            const topText = "weekly bites"; // For meals
            ctx.fillText(topText, padding, padding + fontSize);

            if (dateRangeStr) {
                ctx.font = `600 ${fontSize * 0.6}px Inter, sans-serif`;
                ctx.fillText(dateRangeStr, padding, padding + fontSize * 1.8);
            }
        }

        ctx.restore();
    };

    const REEL_FRAMES = {
        Polaroid: { label: 'Polaroid', icon: '📸', color: '#ffffff' },
        Cinema: { label: 'Cinema', icon: '🎞️', color: '#000000' },
        Neon: { label: 'Neon', icon: '🌈', color: '#ff00ff' },
        Minimal: { label: 'Minimal', icon: '🖼️', color: 'transparent' },
        Retro: { label: 'Retro', icon: '📜', color: '#fdf5e6' },
        Morph: { label: 'Morph', icon: '🌀', color: '#6366f1' }
    };

    const drawReelFrame = (ctx, w, h, frameStyle, progress = 0) => {
        if (frameStyle === 'None' || frameStyle === 'Minimal') return;

        if (frameStyle === 'Morph') {
            const morphFactor = (Math.sin(progress * Math.PI - Math.PI / 2) + 1) / 2;
            const radius = morphFactor * (w / 2.2);

            ctx.strokeStyle = '#6366f1';
            ctx.lineWidth = 8;
            ctx.beginPath();
            ctx.roundRect(30, 30, w - 60, h - 60, radius);
            ctx.stroke();
            return;
        }

        const frame = REEL_FRAMES[frameStyle] || REEL_FRAMES.Minimal;
        ctx.strokeStyle = frame.color;
        ctx.lineWidth = 40;
        ctx.strokeRect(20, 20, w - 40, h - 40);

        if (frameStyle === 'Polaroid') {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, h - 160, w, 160);
        }
    };

    const drawReelImage = (ctx, img, w, h, frameNum, totalFrames, transition, reelFrame) => {
        const progress = frameNum / totalFrames;

        ctx.save();
        // Softer Ken Burns: minimal zoom and pan to keep footer safe
        const zoom = 1 + (progress * 0.08); // Reduced from 0.15
        const panX = (progress * 10) - 5;   // Reduced from 30

        ctx.translate(w / 2 + panX, h / 2);

        const iw = img.naturalWidth;
        const ih = img.naturalHeight;
        const aspect = iw / ih;

        let drawW, drawH;
        // Use "contain" logic for poses to ensure footer is never cropped
        if (reelFrame === 'Minimal' || aspect > w / h) {
            drawW = w;
            drawH = w / aspect;
            // If still taller than frame, scale down to fit height
            if (drawH > h) {
                drawH = h;
                drawW = h * aspect;
            }
        } else {
            drawH = h;
            drawW = h * aspect;
        }

        ctx.scale(zoom, zoom);
        ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
        ctx.restore();
    };


    const generateVideo = useCallback(async (selectedPhotos, reelTransition, reelFrame, dateRangeStr, type = 'meal') => {
        if (isGeneratingReel[type] || selectedPhotos.length === 0) return;

        setIsGeneratingReel(prev => ({ ...prev, [type]: true }));
        setGeneratingReelProgress(prev => ({ ...prev, [type]: { step: 'Preparing photos...', index: 0, percent: 0 } }));
        setReelVideoUrl(prev => ({ ...prev, [type]: null }));
        abortControllerRef.current = new AbortController();

        try {
            const canvas = document.createElement('canvas');
            canvas.width = 1080;
            canvas.height = 1080;
            const ctx = canvas.getContext('2d');

            const stream = canvas.captureStream(30);

            // Try different mime types
            const mimeTypes = [
                'video/webm;codecs=vp9',
                'video/webm;codecs=vp8',
                'video/webm',
                'video/mp4' // fallback for Safari sometimes, usually not supported by MediaRecorder
            ];

            let selectedMime = mimeTypes.find(mime => MediaRecorder.isTypeSupported(mime));

            if (!selectedMime) {
                toast.error('Video rendering not supported in this browser.');
                setIsGeneratingReel(prev => ({ ...prev, [type]: false }));
                return;
            }

            const recorder = new MediaRecorder(stream, {
                mimeType: selectedMime,
                videoBitsPerSecond: 8000000 // 8 Mbps for good quality
            });

            const chunks = [];
            recorder.ondataavailable = e => {
                if (e.data.size > 0) chunks.push(e.data);
            };

            const recordPromise = new Promise((resolve, reject) => {
                recorder.onstop = () => {
                    const extension = selectedMime.includes('mp4') ? 'mp4' : 'webm';
                    const blob = new Blob(chunks, { type: selectedMime });
                    resolve({ blob, extension });
                };
                recorder.onerror = reject;
            });

            recorder.start();

            // Render each photo
            for (let i = 0; i < selectedPhotos.length; i++) {
                if (abortControllerRef.current?.signal.aborted) throw new Error('Generation aborted');

                const photo = selectedPhotos[i];
                const img = new Image();
                img.crossOrigin = 'anonymous';
                // Use photo_url or data URL directly
                img.src = photo.photo_url || photo.image_url || photo;

                await new Promise((res) => {
                    img.onload = res;
                    img.onerror = () => {
                        console.warn('Failed to load image:', photo.photo_url);
                        res();
                    };
                });

                if (!img.complete || img.naturalWidth === 0) continue;

                setGeneratingReelProgress(prev => ({ ...prev, [type]: { step: 'Cooking memories...', index: i, percent: 0 } }));

                const framesPerPhoto = 60; // 2 seconds at 30fps
                for (let f = 0; f < framesPerPhoto; f++) {
                    const progress = f / framesPerPhoto;
                    if (abortControllerRef.current?.signal.aborted) throw new Error('Generation aborted');

                    ctx.fillStyle = '#111827'; // Darker background
                    ctx.fillRect(0, 0, canvas.width, canvas.height);

                    // --- DRAWING PASS ---
                    ctx.save();

                    // Apply Static Morph Mask if needed
                    if (reelFrame === 'Morph' || reelTransition === 'Morph') {
                        // Slow path: Square to Circle over the full 2 seconds
                        const morphFactor = (Math.sin(progress * Math.PI - Math.PI / 2) + 1) / 2;
                        const radius = morphFactor * (canvas.width / 2.2);
                        ctx.beginPath();
                        ctx.roundRect(30, 30, canvas.width - 60, canvas.height - 60, radius);
                        ctx.clip();
                    }

                    // Add transition logic from older implementation
                    if (reelTransition === 'Crossfade' && i > 0 && f < 30) {
                        const prevImg = new Image();
                        prevImg.crossOrigin = 'anonymous';
                        prevImg.src = selectedPhotos[i - 1].photo_url || selectedPhotos[i - 1].image_url || selectedPhotos[i - 1];
                        if (prevImg.complete) {
                            ctx.save();
                            ctx.globalAlpha = 1 - (f / 30);
                            drawReelImage(ctx, prevImg, canvas.width, canvas.height, framesPerPhoto, framesPerPhoto, reelTransition, reelFrame);
                            ctx.restore();
                        }
                    }

                    if (reelTransition === 'Crossfade' && i > 0 && f < 30) {
                        ctx.globalAlpha = (f / 30);
                    } else {
                        ctx.globalAlpha = 1;
                    }

                    drawReelImage(ctx, img, canvas.width, canvas.height, f, framesPerPhoto, reelTransition, reelFrame);
                    ctx.restore();

                    // Draw non-clipping elements
                    drawReelFrame(ctx, canvas.width, canvas.height, reelFrame, progress);
                    drawSessionWatermark(ctx, canvas.width, canvas.height, dateRangeStr, type);

                    // Tiny pause for stream capture
                    await new Promise(r => setTimeout(r, 32));
                }
            }

            recorder.stop();
            const { blob: webmBlob } = await recordPromise;

            setReelVideoUrl(prev => ({ ...prev, [type]: null })); // Clear previous

            // --- FFmpeg conversion to MP4 ---
            let finalBlob = webmBlob;

            setGeneratingReelProgress(prev => ({ ...prev, [type]: { step: 'Applying cinematic touches...', index: selectedPhotos.length - 1, percent: 0 } }));

            try {
                const { FFmpeg } = await import('@ffmpeg/ffmpeg');

                const ffmpeg = new FFmpeg();
                const baseURL = '/ffmpeg-core';

                // Add logging so we can see if it hangs
                ffmpeg.on('log', ({ message }) => {
                    console.log('[FFmpeg]', message);
                });

                let lastProgress = 0;
                ffmpeg.on('progress', ({ progress }) => {
                    const percent = Math.round(progress * 100);
                    // Throttle state updates to every 5% to prevent React from hanging the thread
                    if (percent >= lastProgress + 5 || percent === 100) {
                        lastProgress = percent;
                        setGeneratingReelProgress(prev => ({ ...prev, [type]: { ...prev[type], percent } }));
                    }
                });

                await ffmpeg.load({
                    coreURL: `${baseURL}/ffmpeg-core.js`,
                    wasmURL: `${baseURL}/ffmpeg-core.wasm`,
                });

                // Convert Blob directly to Uint8Array instead of using fetchFile to avoid module resolution errors
                const videoData = new Uint8Array(await webmBlob.arrayBuffer());
                await ffmpeg.writeFile('input.webm', videoData);

                // Fetch and write audio - Use native fetch to get ArrayBuffer
                const musicPath = type === 'pose' ? '/music/meditation.mp3' : '/music/meal.mp3';
                const audioRes = await fetch(musicPath);
                const audioBuffer = await audioRes.arrayBuffer();
                await ffmpeg.writeFile('bgm.mp3', new Uint8Array(audioBuffer));

                // Execute conversion and merge audio
                // -shortest ensures the video doesn't loop or extend indefinitely if audio is longer
                // QuickTime compatibility: -c:v libx264 -pix_fmt yuv420p -profile:v main -level 3.1 -movflags +faststart
                await ffmpeg.exec([
                    '-r', '30',            // Input frame rate
                    '-i', 'input.webm',
                    '-i', 'bgm.mp3',
                    '-c:v', 'libx264',
                    '-preset', 'ultrafast',
                    '-threads', '1',
                    '-profile:v', 'main',
                    '-level', '3.1',
                    '-pix_fmt', 'yuv420p',
                    '-r', '30',            // Output frame rate
                    '-c:a', 'aac',
                    '-b:a', '128k',
                    '-map', '0:v:0',
                    '-map', '1:a:0',
                    '-shortest',
                    '-movflags', '+faststart',
                    'output.mp4'
                ]);

                // Read the output MP4
                const mp4Data = await ffmpeg.readFile('output.mp4');
                finalBlob = new Blob([mp4Data.buffer], { type: 'video/mp4' });

                // Add metadata stripping
                setGeneratingReelProgress(prev => ({ ...prev, [type]: { step: 'Optimizing final video...', index: selectedPhotos.length - 1, percent: 95 } }));

                // Write our clean mp4 back to the virtual FS
                await ffmpeg.writeFile('clean_input.mp4', mp4Data);

                // Run a second pass to strip metadata and add metadata title (important for QuickTime / Apple ecosystem)
                await ffmpeg.exec([
                    '-i', 'clean_input.mp4',
                    '-c', 'copy',          // Copy video/audio codecs without re-encoding
                    '-map_metadata', '-1', // Strip global metadata
                    '-metadata', `title="${type === 'pose' ? 'Session Highlights' : 'Weekly Bites'}"`,
                    'final_output.mp4'
                ]);

                // Read the final optimized MP4
                const finalMp4Data = await ffmpeg.readFile('final_output.mp4');
                finalBlob = new Blob([finalMp4Data.buffer], { type: 'video/mp4' });

                // Clean up ffmpeg FS to free memory
                try {
                    await ffmpeg.deleteFile('input.webm');
                    await ffmpeg.deleteFile('bgm.mp3');
                    await ffmpeg.deleteFile('output.mp4');
                    await ffmpeg.deleteFile('clean_input.mp4');
                    await ffmpeg.deleteFile('final_output.mp4');
                } catch (e) { console.warn("Error cleaning up ffmpeg files", e); }

            } catch (ffmpegErr) {
                console.error("FFmpeg fallback needed:", ffmpegErr);
                toast.error('Premium cinematic export failed. Please try again.');
                throw ffmpegErr;
            }

            const url = URL.createObjectURL(finalBlob);
            setReelVideoUrl(prev => ({ ...prev, [type]: url }));
            toast.success(`Your ${type === 'pose' ? 'Session Highlights' : 'Weekly Bites'} is ready!`);
        } catch (err) {
            if (err.message === 'Generation aborted') {
                console.log('Video generation aborted by user.');
            } else {
                console.error('Reel generation error:', err);
                toast.error('Failed to generate video.');
            }
        } finally {
            setIsGeneratingReel(prev => ({ ...prev, [type]: false }));
            abortControllerRef.current = null;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGeneratingReel]);

    const cancelGeneration = useCallback((type = 'meal') => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            setIsGeneratingReel(prev => ({ ...prev, [type]: false }));
            setGeneratingReelProgress(prev => ({ ...prev, [type]: { step: '', index: 0, percent: 0 } }));
            toast('Video generation cancelled.', { icon: '🛑' });
        }
    }, []);

    const resetVideo = useCallback((type = 'meal') => {
        setReelVideoUrl(prev => ({ ...prev, [type]: null }));
    }, []);

    const updateGlobalShots = useCallback((type, shots, selectedIds) => {
        setGlobalSessionShots(prev => ({ ...prev, [type]: shots }));
        if (selectedIds) {
            setGlobalSelectedShots(prev => ({ ...prev, [type]: selectedIds }));
        }
    }, []);

    return (
        <VideoGenerationContext.Provider value={{
            isGeneratingReel,
            generatingReelProgress,
            reelVideoUrl,
            globalSessionShots,
            globalSelectedShots,
            generateVideo,
            cancelGeneration,
            resetVideo,
            updateGlobalShots
        }}>
            {children}
        </VideoGenerationContext.Provider>
    );
};
